import { Box, Heading, Text, VStack } from "@biblioteksentralen/react";
import { digitalLibraryServiceCategories, richTextHasContent } from "@libry-content/common";
import { getPath } from "@libry-content/common";
import { TranslateMicroText, useTranslation } from "../../utils/hooks/useTranslation";
import { DefaultContainer } from "../ContentContainer";
import BlockContent from "../blockContent/BlockContent";
import { CommonLinkStyling } from "../blockContent/LinkSerializer";
import { Edit } from "../editInSanity/EditInSanity";
import { SanityImageWithResponsiveSize } from "../images/SanityImageWithResponsiveSize";
import { ResolvedDigitalLibraryService } from "@libry-content/common";
import { useDigitalServiceColor } from "./useDigitalServiceColor";
import {
  isAllbok,
  isAuthenticatedService,
  isBookbites,
  isDigitaltNasjonaltLanekort,
  isFilmoteket,
  isLibby,
  isNaxos,
} from "./utils";

type DigitalLibraryServiceProps = {
  digitalLibraryService: ResolvedDigitalLibraryService | undefined;
};

export const DigitalLibraryServiceView = ({ digitalLibraryService }: DigitalLibraryServiceProps) => {
  const { t, ts } = useTranslation();
  const { category, name, teaser, description, getStarted, support, image } = digitalLibraryService ?? {};
  const hasImage = !!image?.asset;
  const fallbackColor = useDigitalServiceColor(digitalLibraryService);

  if (!digitalLibraryService || !category) return null;

  const bannerBackground = hasImage ? undefined : fallbackColor;

  return (
    <>
      <Box width="100%" {...bannerBackground?.css} padding={hasImage ? "1rem 0 0" : "2rem 0"}>
        <DefaultContainer maxWidth={hasImage ? undefined : "32rem"} margin="0 auto">
          <VStack alignItems="flex-start">
            <Text fontWeight="semibold" fontSize=".8rem">
              {ts(digitalLibraryServiceCategories[category])}
            </Text>
            <Heading as="h1">{ts(name)}</Heading>
            <Edit doc={digitalLibraryService} />
            <Text fontSize="lg" maxWidth="30em">
              {ts(teaser)}
            </Text>
          </VStack>
        </DefaultContainer>
      </Box>
      <DefaultContainer>
        <VStack alignItems="flex-start" margin={{ base: "1rem 0 2rem", md: "2rem 0 3rem" }}>
          {image?.asset && (
            <Box marginBottom="2rem">
              <SanityImageWithResponsiveSize
                image={image}
                aspectRatio={1.95}
                resolution={{ base: 540, sm: 1080, lg: 2160 }}
              />
            </Box>
          )}
          <Box maxWidth="30rem" margin="0rem auto 2rem" fontSize="lg">
            {richTextHasContent(ts(description)) && <BlockContent blocks={ts(description)} />}
            <AdditionalFieldsAfterDescription digitalLibraryService={digitalLibraryService} />
            {!!richTextHasContent(ts(getStarted)) && (
              <>
                <Heading as="h3" fontSize="2xl" marginBottom="0.25rem" marginTop="calc(2vmin + 2rem)">
                  {t("Kom i gang")}
                </Heading>
                <AdditionalFieldsBeforeGetStarted digitalLibraryService={digitalLibraryService} />
                <BlockContent blocks={ts(getStarted)} />
              </>
            )}
            <AdditionalFieldsAfterGetStarted digitalLibraryService={digitalLibraryService} />
            {!!support?.url && !!ts(support.linkText) && (
              <Text>
                {ts(support.preface) && <Text as="span">{ts(support.preface)} </Text>}
                <CommonLinkStyling href={support.url}>{ts(support.linkText)}</CommonLinkStyling>
              </Text>
            )}
          </Box>
        </VStack>
      </DefaultContainer>
    </>
  );
};

const UrlWithLinkText = ({ text, url }: { text: string; url: string }) => {
  const textBeforeUrl = text.replace(/<<.+/, "");
  const urlText = text.match(/<<(.+)>>/)?.[1];
  const textAfterUrl = text.replace(/.+>>/, "");

  return (
    <Text marginBottom="1.75rem">
      {!!textBeforeUrl && <Text as="span">{textBeforeUrl}</Text>}
      {!!urlText && <CommonLinkStyling href={url}>{urlText}</CommonLinkStyling>}
      {!!textAfterUrl && <Text as="span">{textAfterUrl}</Text>}
    </Text>
  );
};

const AdditionalFieldsAfterDescription = ({ digitalLibraryService }: DigitalLibraryServiceProps) => {
  const { ts } = useTranslation();

  // Digitalt bibliotekkort
  if (
    isDigitaltNasjonaltLanekort(digitalLibraryService) &&
    !!digitalLibraryService.digitaltNasjonaltLanekortCanBeUsedWithSelfService &&
    !!ts(digitalLibraryService.digitaltNasjonaltLanekortCanBeUsedWithSelfServiceText)
  ) {
    return <Text>{ts(digitalLibraryService.digitaltNasjonaltLanekortCanBeUsedWithSelfServiceText)}</Text>;
  }

  // Filmoteket
  if (
    isFilmoteket(digitalLibraryService) &&
    typeof digitalLibraryService.filmoteketNumberOfMoviesPerMonth === "number" &&
    !!ts(digitalLibraryService.filmoteketnumberOfMoviesPerMonthText)
  ) {
    return (
      <Text>
        {ts(digitalLibraryService.filmoteketnumberOfMoviesPerMonthText)?.replace(
          /{{tall}}/g,
          digitalLibraryService.filmoteketNumberOfMoviesPerMonth.toString()
        )}
      </Text>
    );
  }

  // Libby
  if (
    isLibby(digitalLibraryService) &&
    typeof digitalLibraryService.libbyBookBorrowingDays === "number" &&
    typeof digitalLibraryService.libbyNumberOfConcurrentBooks === "number" &&
    !!ts(digitalLibraryService.libbyBorrowingDaysAndConcurrentBooksText)
  ) {
    return (
      <Text>
        {ts(digitalLibraryService.libbyBorrowingDaysAndConcurrentBooksText)
          ?.replace(/{{dager}}/g, digitalLibraryService.libbyBookBorrowingDays.toString())
          ?.replace(/{{bøker}}/g, digitalLibraryService.libbyNumberOfConcurrentBooks.toString())}
      </Text>
    );
  }

  // Allbok
  if (
    isAllbok(digitalLibraryService) &&
    typeof digitalLibraryService.allbokEbookBorrowingDays === "number" &&
    typeof digitalLibraryService.allbokNumberOfConcurrentEbooks === "number" &&
    typeof digitalLibraryService.allbokNumberOfConcurrentAudiobooks === "number" &&
    !!ts(digitalLibraryService.allbokBorrowingDaysAndConcurrentBooksText)
  ) {
    return (
      <Text>
        {ts(digitalLibraryService.allbokBorrowingDaysAndConcurrentBooksText)
          ?.replace(/{{dager}}/g, digitalLibraryService.allbokEbookBorrowingDays.toString())
          ?.replace(/{{ebøker}}/g, digitalLibraryService.allbokNumberOfConcurrentEbooks.toString())
          ?.replace(/{{lydbøker}}/g, digitalLibraryService.allbokNumberOfConcurrentAudiobooks.toString())}
      </Text>
    );
  }

  return null;
};

const AdditionalFieldsBeforeGetStarted = ({ digitalLibraryService }: DigitalLibraryServiceProps) => {
  const { ts, t } = useTranslation();
  if (isAuthenticatedService(digitalLibraryService)) {
    return (
      <Text marginBottom="1.75rem">
        <CommonLinkStyling href={getPath(digitalLibraryService!) + "/tilgang"}>
          {t("Gå til {tjeneste}", { tjeneste: ts(digitalLibraryService.name) })}
        </CommonLinkStyling>
      </Text>
    );
  }

  // Naxos
  if (
    isNaxos(digitalLibraryService) &&
    typeof digitalLibraryService.naxosPageUrl === "string" &&
    !!ts(digitalLibraryService.naxosPageUrlText)
  ) {
    return (
      <UrlWithLinkText text={ts(digitalLibraryService.naxosPageUrlText)!} url={digitalLibraryService.naxosPageUrl} />
    );
  }

  return null;
};

const getSchoolsText = (
  t: TranslateMicroText,
  { primarySchool, secondarySchool }: { primarySchool?: boolean; secondarySchool?: boolean }
): string => {
  const schools: string[] = [];

  if (primarySchool) {
    schools.push(t("grunnskole"));
  }

  if (secondarySchool) {
    schools.push(t("videregående skole"));
  }

  return schools.join(` ${t("eller")} `);
};

const AdditionalFieldsAfterGetStarted = ({ digitalLibraryService }: DigitalLibraryServiceProps) => {
  const { ts, t } = useTranslation();

  // BookBites
  if (
    isBookbites(digitalLibraryService) &&
    typeof digitalLibraryService.bookbitesPrimarySchoolCanLogInWithFeide === "boolean" &&
    ts(digitalLibraryService.bookbitesLogInWithFeideText)
  ) {
    const schoolsText = getSchoolsText(t, {
      primarySchool: digitalLibraryService.bookbitesPrimarySchoolCanLogInWithFeide,
      secondarySchool: true,
    });
    return (
      <Text marginBottom="1.75rem">
        {ts(digitalLibraryService.bookbitesLogInWithFeideText)?.replace(/{{skoler}}/g, schoolsText)}
      </Text>
    );
  }

  // Libby
  if (isLibby(digitalLibraryService) && digitalLibraryService.libbyPageUrl && ts(digitalLibraryService.libbyUrlText)) {
    return <UrlWithLinkText text={ts(digitalLibraryService.libbyUrlText)!} url={digitalLibraryService.libbyPageUrl} />;
  }

  // Allbok
  if (
    isAllbok(digitalLibraryService) &&
    (digitalLibraryService.allbokPrimarySchoolCanLogInWithFeide ||
      digitalLibraryService.allbokSecondarySchoolCanLogInWithFeide) &&
    ts(digitalLibraryService.allbokLogInWithFeideText)
  ) {
    const schoolsText = getSchoolsText(t, {
      primarySchool: digitalLibraryService.allbokPrimarySchoolCanLogInWithFeide,
      secondarySchool: digitalLibraryService.allbokSecondarySchoolCanLogInWithFeide,
    });

    return (
      <Text marginBottom="1.75rem">
        {ts(digitalLibraryService.allbokLogInWithFeideText)?.replace(/{{skoler}}/g, schoolsText)}
      </Text>
    );
  }

  // Naxos
  if (
    isNaxos(digitalLibraryService) &&
    typeof digitalLibraryService.naxosNumberOfConcurrentUsers === "number" &&
    !!ts(digitalLibraryService.naxosNumberOfConcurrentUsersText)
  ) {
    return (
      <Text>
        {ts(digitalLibraryService.naxosNumberOfConcurrentUsersText)?.replace(
          /{{lånere}}/g,
          digitalLibraryService.naxosNumberOfConcurrentUsers.toString()
        )}
      </Text>
    );
  }

  return null;
};
